import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { API } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { resolveImage } from "@/lib/siteDefaults";

export const cApi = axios.create({ baseURL: API, withCredentials: true });

export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace("Value error, ", "")).join(", ");
  return d || fallback;
};

export function useCommunityMe() {
  const { user, loading: authLoading } = useCustomerAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    if (!user) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await cApi.get("/community/me");
      setMe(data);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [user]);
  useEffect(() => {
    if (!authLoading) reload();
  }, [authLoading, reload]);
  return { user, me, level: me?.access_level || "visitor", loading: authLoading || loading, reload };
}

export const img = (u) => (u ? resolveImage(u) : u);

export const fmtDate = (iso, opts = { month: "long", day: "numeric", year: "numeric" }) =>
  iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, opts) : "";

export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";

export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export const ELIGIBILITY_LABEL = {
  public: "Open to all",
  members: "Jenova Girls",
  prive: "Privé members only",
};

export const SET_REQUIREMENT =
  "To become a Jenova Girl and join the Privé channel, purchase one Jenova set — a three-piece legging set or a short set — from our shop.";
export const PRIVE_SHOP_URL = "/shop?prive=1";

let priveIdsCache = null;
export function usePriveProductIds() {
  const [ids, setIds] = useState(null);
  useEffect(() => {
    priveIdsCache = priveIdsCache || cApi.get("/community/prive/qualifying-products").then(({ data }) => data.product_ids).catch(() => { priveIdsCache = null; return []; });
    let live = true;
    priveIdsCache.then((v) => live && setIds(v));
    return () => { live = false; };
  }, []);
  return ids;
}


export const STOCK = {
  hero: "https://images.unsplash.com/photo-1560821829-18a5fbb8b4ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  founder: "https://images.unsplash.com/photo-1763539818420-165e69b7489b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sisterhood: "https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  prive: "https://images.unsplash.com/photo-1779406976648-60d315100824?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  memories: [
    "https://images.unsplash.com/photo-1788463127214-8bf0fcfda46d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1519671282429-b44660ead0a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1753098498024-c2a51a8e021c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1713627242632-c003a3ae6bcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1536010305525-f7aa0834e2c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1630595271375-5073a6c0638b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  ],
};

export const EVENT_FALLBACK_IMG = {
  "Pilates & Wellness": "https://images.unsplash.com/photo-1763403921315-f2ef8697199f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Girls' Brunch": "https://images.unsplash.com/photo-1519671282429-b44660ead0a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Sip & Paint": "https://images.unsplash.com/photo-1753098498024-c2a51a8e021c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  Photoshoot: "https://images.unsplash.com/photo-1779406976648-60d315100824?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Beauty & Self-Care": "https://images.unsplash.com/photo-1630595271375-5073a6c0638b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Faith & Bible Study": "https://images.unsplash.com/photo-1599249300635-da2e60b2a0ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  Networking: "https://images.unsplash.com/photo-1699730148132-1409a3728479?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Travel Experience": "https://images.unsplash.com/photo-1560821829-18a5fbb8b4ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Community Gathering": "https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

export const eventImg = (e) => img(e?.cover_image) || EVENT_FALLBACK_IMG[e?.category] || STOCK.hero;

export const INTERESTS = ["Wellness & fitness", "Faith & spiritual growth", "Beauty & self-care", "Business & entrepreneurship",
  "Creativity & art", "Travel", "Photography", "Friendship & connection", "Personal growth", "AI & technology"];

export const ACTIVITIES = ["Pilates & wellness days", "Girls' brunches", "Sip & paint", "Photoshoots", "Beauty & self-care days",
  "Bible studies & faith conversations", "Networking events", "Travel experiences", "Community gatherings"];

export const DEFAULT_CONTENT = {
  hero_headline: "More than a community. A sisterhood.",
  hero_description:
    "A space for women to grow, connect, create memories, embrace femininity, prioritize wellness, and become the women God called them to be.",
  hero_image: "",
  founder_name: "Jennifer Campbell",
  founder_title: "Founder of Jenova Collective",
  founder_message:
    "Welcome to Jenova, beautiful!\n\nI’m Jennifer, founder of Jenova Collective. I created this space to celebrate sisterhood, femininity, wellness, faith, and meaningful connections.\n\nMore than a brand, Jenova is a community where women grow, connect, and create memories together. Whether you’re here for friendship, wellness, personal growth, faith, or simply to enjoy girlhood, there’s a place for you here.\n\nSo happy you’re here. Welcome to girlhood!",
  founder_image: "",
  founder_video_url: "",
  intro_title: "Welcome to the Jenova community",
  intro_body:
    "Jenova Collective is a women's lifestyle brand built around femininity, sisterhood, confidence, wellness, faith, personal growth, and meaningful experiences. Here, women connect, make lifelong friendships, attend curated activities, support one another and grow together.",
  memory_images: [],
};
